import React, { useMemo } from 'react';

import { Typography, Grid } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import NotificationsIcon from '@material-ui/icons/Notifications';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import WarningIcon from '@material-ui/icons/Warning';
import { format, isPast } from 'date-fns';
import { useTranslation } from 'react-i18next';

import RichTextContent from 'components/RichTextContent/RichTextContent';
import type { ActionLibraryInstance } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';
import assets_messages from 'messages/assets_messages';

import useStyles from './ActionLibraryInstancePrintTemplate.styles';

type Props = {
  instance: ActionLibraryInstance;
};

const ActionLibraryInstancePrintTemplate: React.FC<Props> = ({ instance }) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const deadlineDate = instance.deadline ? new Date(instance.deadline) : null;
  const reminderDate = instance.reminder ? new Date(instance.reminder) : null;
  const isDeadlinePast = deadlineDate && isPast(deadlineDate) && instance.status !== 'completed';
  const isReminderPast = reminderDate && isPast(reminderDate) && instance.status !== 'completed';

  const statusData = useMemo(() => {
    let StatusIconComponent: React.ComponentType<{ className?: string }>;
    let statusIconClass: string;
    if (instance.status === 'completed') {
      StatusIconComponent = CheckCircleIcon;
      statusIconClass = styles.activeIcon;
    } else if (instance.status === 'in_progress') {
      StatusIconComponent = RemoveCircleOutlineIcon;
      statusIconClass = styles.criticalityMediumIcon;
    } else {
      StatusIconComponent = CancelIcon;
      statusIconClass = styles.inactiveIcon;
    }

    return {
      StatusIconComponent,
      statusIconClass,
      displayValue: instance.status,
    };
  }, [instance.status, styles]);

  const criticalityData = useMemo(() => {
    const criticalityKey = instance.criticality.toUpperCase() as 'LOW' | 'MEDIUM' | 'HIGH';
    let CriticalityIconComponent: React.ComponentType<{ className?: string }>;
    let criticalityIconClass: string;

    if (instance.criticality === 'low') {
      CriticalityIconComponent = InfoIcon;
      criticalityIconClass = styles.criticalityLowIcon;
    } else if (instance.criticality === 'medium') {
      CriticalityIconComponent = WarningIcon;
      criticalityIconClass = styles.criticalityMediumIcon;
    } else {
      CriticalityIconComponent = ErrorIcon;
      criticalityIconClass = styles.criticalityHighIcon;
    }

    return {
      CriticalityIconComponent,
      criticalityIconClass,
      displayValue: t(action_library_messages.criticality[criticalityKey]),
    };
  }, [instance.criticality, t, styles]);

  const StatusIcon = statusData.StatusIconComponent;
  const CriticalityIcon = criticalityData.CriticalityIconComponent;

  return (
    <div className={styles.root}>
      <Typography className={styles.title} variant='h3'>
        {instance.title}
      </Typography>

      <div className={styles.section}>
        <div className={styles.value}>{instance.description ? <RichTextContent content={instance.description} /> : '-'}</div>
      </div>

      <Grid container spacing={4}>
        <Grid item xs={6}>
          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.instance.detail.status)}
            </Typography>
            <Typography className={styles.value} variant='body1'>
              <div className={styles.valueWithIcon}>
                <StatusIcon className={`${styles.statusIcon} ${statusData.statusIconClass}`} />
                <span>{statusData.displayValue}</span>
              </div>
            </Typography>
          </div>

          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.instance.detail.asset_name)}
            </Typography>
            <Typography className={styles.value} variant='body1'>
              {instance.assetName || '-'}
            </Typography>
          </div>

          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.instance.detail.owner)}
            </Typography>
            <Typography className={styles.value} variant='body1'>
              {instance.ownerFullName || '-'}
            </Typography>
          </div>

          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.instance.detail.deadline)}
            </Typography>
            <Typography
              className={styles.value}
              style={{ color: isDeadlinePast ? '#f44336' : 'inherit', fontWeight: isDeadlinePast ? 'bold' : 'normal' }}
              variant='body1'
            >
              {deadlineDate ? format(deadlineDate, 'yyyy-MM-dd HH:mm') : '-'}
            </Typography>
          </div>

          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.instance.detail.status_percent)}
            </Typography>
            <Typography className={styles.value} variant='body1'>
              {`${instance.statusPercent || 0}%`}
            </Typography>
          </div>
        </Grid>

        <Grid item xs={6}>
          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.instance.detail.criticality)}
            </Typography>
            <Typography className={styles.value} variant='body1'>
              <div className={styles.valueWithIcon}>
                <CriticalityIcon className={`${styles.statusIcon} ${criticalityData.criticalityIconClass}`} />
                <span>{criticalityData.displayValue}</span>
              </div>
            </Typography>
          </div>

          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.instance.detail.asset_type)}
            </Typography>
            <Typography className={styles.value} variant='body1'>
              {instance.assetType || '-'}
            </Typography>
          </div>

          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.instance.detail.asset_importance)}
            </Typography>
            <Typography className={styles.value} variant='body1'>
              {instance.assetImportance
                ? // @ts-ignore
                  t(assets_messages.information_choices_assets_importance[instance.assetImportance]) || instance.assetImportance
                : '-'}
            </Typography>
          </div>

          {instance.delegatedToFullName && (
            <div className={styles.section}>
              <Typography className={styles.label} variant='body2'>
                {t(action_library_messages.instance.detail.delegated_to)}
              </Typography>
              <Typography className={styles.value} variant='body1'>
                {instance.delegatedToFullName}
              </Typography>
            </div>
          )}

          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.instance.detail.reminder)}
            </Typography>
            <Typography className={styles.value} style={{ color: isReminderPast ? '#ff9800' : 'inherit' }} variant='body1'>
              {reminderDate ? (
                <div className={styles.valueWithIcon}>
                  <NotificationsIcon style={{ fontSize: '1rem', color: isReminderPast ? '#ff9800' : 'rgba(0, 0, 0, 0.54)' }} />
                  <span>{format(reminderDate, 'yyyy-MM-dd HH:mm')}</span>
                </div>
              ) : (
                '-'
              )}
            </Typography>
          </div>
        </Grid>
      </Grid>

      {instance.specialInstruction && (
        <>
          <div className={styles.divider} />
          <div className={styles.section}>
            <Typography className={styles.label} variant='body2'>
              {t(action_library_messages.instance.detail.special_instruction)}
            </Typography>
            <Typography className={styles.value} variant='body1'>
              <RichTextContent content={instance.specialInstruction} />
            </Typography>
          </div>
        </>
      )}
    </div>
  );
};

export default ActionLibraryInstancePrintTemplate;
